import type { FC } from 'react';
import type { ITracker, TrackerConfig } from './types';
import type { TAnalyticsItem } from '../types';

declare global {
  interface Window {
    kiwi?: {
      init: (appId: string, token: string, config: Record<string, unknown>) => void;
      identify: (userId: string, traits: Record<string, unknown>) => void;
      track: (event: string, properties: Record<string, unknown>) => void;
    };
  }
}

// Get currency from item or fall back to channel default
const getCurrency = (item: TAnalyticsItem, fallback: string) => item.currency || fallback;
const getCurrencyFromItems = (items: TAnalyticsItem[], fallback: string) =>
  items[0]?.currency || fallback;

export const createInteraktTracker = (token: string, config: TrackerConfig): ITracker => {
  const { channel, debug } = config;
  const defaultCurrency = channel.defaultCurrency;

  const track = (event: string, data: Record<string, unknown>) => {
    if (typeof window === 'undefined' || !window.kiwi) {
      if (debug) {
        console.warn('[Interakt] window.kiwi not available');
      }
      return;
    }
    window.kiwi.track(event, data);
    if (debug) {
      console.log('[Interakt]', event, data);
    }
  };

  return {
    init() {
      if (typeof window === 'undefined') return;
      if (debug) {
        console.log('[Interakt] Initialized with token');
      }
    },

    trackPageView({ url }) {
      track('Page Viewed', { url });
    },

    identify({ email, data }) {
      if (typeof window === 'undefined' || !window.kiwi) {
        if (debug) {
          console.warn('[Interakt] window.kiwi not available for identify');
        }
        return;
      }
      window.kiwi.identify(email, { email, ...data });
      if (debug) {
        console.log('[Interakt] Identify', email);
      }
    },

    trackViewItem({ item }) {
      track('Product Viewed', {
        product_id: item.item_id,
        product_name: item.item_name,
        price: item.price,
        currency: getCurrency(item, defaultCurrency),
      });
    },

    trackViewItemList({ items, listId, listName }) {
      track('Category Viewed', {
        category_id: listId,
        category_name: listName,
        product_count: items.length,
        currency: getCurrencyFromItems(items, defaultCurrency),
      });
    },

    trackAddToCart({ item, quantity, value }) {
      track('Added to Cart', {
        product_id: item.item_id,
        product_name: item.item_name,
        quantity,
        value,
        currency: getCurrency(item, defaultCurrency),
      });
    },

    trackRemoveFromCart({ item, quantity, value }) {
      track('Removed from Cart', {
        product_id: item.item_id,
        product_name: item.item_name,
        quantity,
        value,
        currency: getCurrency(item, defaultCurrency),
      });
    },

    trackViewCart({ items, value }) {
      track('Cart Viewed', {
        item_count: items.length,
        value,
        currency: getCurrencyFromItems(items, defaultCurrency),
      });
    },

    trackBeginCheckout({ items, value }) {
      track('Checkout Started', {
        item_count: items.length,
        value,
        currency: getCurrencyFromItems(items, defaultCurrency),
      });
    },

    trackAddShippingInfo({ items, value, shippingTier }) {
      track('Shipping Info Added', {
        value,
        currency: getCurrencyFromItems(items, defaultCurrency),
        shipping_tier: shippingTier,
      });
    },

    trackAddPaymentInfo({ items, value, paymentType }) {
      track('Payment Info Added', {
        value,
        currency: getCurrencyFromItems(items, defaultCurrency),
        payment_type: paymentType,
      });
    },

    trackPurchase({ transactionId, items, value, tax, shipping }) {
      track('Order Placed', {
        order_id: transactionId,
        value,
        currency: getCurrencyFromItems(items, defaultCurrency),
        tax,
        shipping,
        item_count: items.length,
      });
    },

    trackAddToWaitlist({ email, item, value }) {
      track('Waitlist Joined', {
        email,
        product_id: item.item_id,
        product_name: item.item_name,
        value,
        currency: getCurrency(item, defaultCurrency),
      });
    },

    trackEvent({ event, data }) {
      track(event, data || {});
    },
  };
};

interface InteraktScriptProps {
  token: string;
}

export const InteraktScript: FC<InteraktScriptProps> = ({ token }) => {
  return (
    <script
      dangerouslySetInnerHTML={{
        __html: `(function(w,d,s,c,r,a,m){
          w['KiwiObject']=r;
          w[r]=w[r] || function () {
            (w[r].q=w[r].q||[]).push(arguments)};
          w[r].l=1*new Date();
            a=d.createElement(s);
            m=d.getElementsByTagName(s)[0];
          a.async=1;
          a.src=c;
          m.parentNode.insertBefore(a,m)
        })(window,document,'script',"https://app.interakt.ai/kiwi-sdk/kiwi-sdk-17-prod-min.js?v="+ new Date().getTime(),'kiwi');
        window.addEventListener("load",function () {
          kiwi.init('', '${token}', {});
        });`,
      }}
    />
  );
};
